import type { Theme } from '@emotion/react';
import styled from '@emotion/styled';
import type { CSSProperties } from 'react';

import { createClassNames } from '@hrworks/design-system/functions';
import { mq } from '@hrworks/design-system/utilities';

import { IconButtonStyles } from '../IconButton';
import type { AlertProps } from './Alert.types';

const getAlertColorMap = (
  theme: Theme,
): Record<NonNullable<AlertProps['color']>, Pick<CSSProperties, 'color' | 'backgroundColor'>> => ({
  primary: {
    color: theme.eclipseTier2Color.text.brand.default,
    backgroundColor: theme.eclipseTier2Color.background.brand.subtle.hovered,
  },
  secondary: {
    color: theme.eclipseTier2Color.text.default,
    backgroundColor: theme.eclipseTier2Color.background.neutral.subtle.default,
  },
  info: {
    color: theme.eclipseTier2Color.text.info.onSubtle,
    backgroundColor: theme.eclipseTier2Color.background.info.subtle,
  },
  success: {
    color: theme.eclipseTier2Color.text.success.onSubtle,
    backgroundColor: theme.eclipseTier2Color.background.success.subtle,
  },
  warning: {
    color: theme.eclipseTier2Color.text.warning.onSubtle,
    backgroundColor: theme.eclipseTier2Color.background.warning.subtle,
  },
  danger: {
    color: theme.eclipseTier2Color.text.error.onSubtle,
    backgroundColor: theme.eclipseTier2Color.background.error.subtle,
  },
});

type ContainerProps = Pick<Required<AlertProps>, 'corner'> & {
  $color: Required<AlertProps>['color'];
};

const Container = styled.div<ContainerProps>(({ theme, corner, $color }) => {
  const alertColorMap = getAlertColorMap(theme);

  return {
    display: 'flex',
    alignItems: 'center',
    padding: theme.eclipseTier1.spacing['16px'],
    gap: theme.eclipseTier1.spacing['16px'],
    color: alertColorMap[$color].color,
    backgroundColor: alertColorMap[$color].backgroundColor,
    ...(corner === 'rounded' && { borderRadius: theme.eclipseTier1.radius['12px'] }),
  };
});

const Content = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  flex: 1,
  gap: theme.eclipseTier1.spacing['16px'],
}));

const ButtonWrapper = styled.div<Pick<AlertProps, 'corner'>>(({ theme, corner }) => ({
  marginRight: `calc(-1 * ${theme.eclipseTier1.spacing['12px']})`,
  marginTop: `calc(-1 * ${theme.eclipseTier1.spacing['12px']})`,
  alignSelf: 'start',

  [`${IconButtonStyles.sel.Button}`]: {
    ...(corner === 'rounded' && { borderRadius: theme.eclipseTier1.radius['8px'] }),
    color: 'currentColor',
    opacity: theme.variables.opacity.high,
    outlineOffset: -1,
    '> *': {
      fontSize: '1em',
    },
    [mq.supportsHover]: {
      ':hover': {
        opacity: 1,
      },
    },
  },
}));

const components = { Container, Content, ButtonWrapper };

export const S = {
  ...components,
  ...createClassNames('Alert', components),
} as const;
